/**
 * "Rest is over" notifications via Web Push. Enabled per device in the
 * profile; the trainer then asks the server for a push whenever the app goes
 * to the background during a rest (phones pause the page's own timer).
 */
import { api, call } from "./api";

const ENABLED_KEY = "liftquest.push";

export type PushSupport = "supported" | "insecure" | "install" | "unsupported";

/** Why notifications are (not) possible here. iOS only allows them for apps added to the home screen. */
export function pushSupport(): PushSupport {
  if (!window.isSecureContext) return "insecure";
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if ("serviceWorker" in navigator && "PushManager" in window && "Notification" in window) return "supported";
  return ios && !standalone ? "install" : "unsupported";
}

function readFlag() {
  try {
    return localStorage.getItem(ENABLED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeFlag(on: boolean) {
  try {
    if (on) localStorage.setItem(ENABLED_KEY, "1");
    else localStorage.removeItem(ENABLED_KEY);
  } catch {
    // ignore
  }
}

/** Whether this device has a live subscription (also repairs the local flag). */
export async function pushEnabled() {
  if (pushSupport() !== "supported") return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  const on = !!sub && Notification.permission === "granted";
  writeFlag(on);
  return on;
}

function base64UrlToBytes(value: string) {
  const base64 = (value + "=".repeat((4 - (value.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

/** Asks for permission and registers this device. Resolves to false when the user declined. */
export async function enablePush() {
  // No registration in development builds – `ready` would wait forever.
  if (!(await navigator.serviceWorker.getRegistration())) throw new Error("No service worker");
  if ((await Notification.requestPermission()) !== "granted") return false;
  const reg = await navigator.serviceWorker.ready;
  const { publicKey } = await call(api.push.key.$get());
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) }));
  const { endpoint, keys } = sub.toJSON();
  if (!endpoint || !keys?.p256dh || !keys.auth) throw new Error("Incomplete push subscription");
  await call(api.push.subscriptions.$post({ json: { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } } }));
  writeFlag(true);
  return true;
}

export async function disablePush() {
  writeFlag(false);
  if (pushSupport() !== "supported") return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await call(api.push.subscriptions.$delete({ json: { endpoint: sub.endpoint } })).catch(() => {});
  await sub.unsubscribe();
}

/** Server-side alarm; `keepalive` lets the request finish while the page is being hidden. */
export function scheduleRestAlarm(alarm: { endsAt: number; title: string; body: string; url: string }) {
  if (!readFlag()) return;
  void api["rest-alarm"].$put({ json: alarm }, { init: { keepalive: true } }).catch(() => {});
}

export function cancelRestAlarm() {
  if (!readFlag()) return;
  void api["rest-alarm"].$delete(undefined, { init: { keepalive: true } }).catch(() => {});
}
